import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  FormHelperText,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  OutlinedInput,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { demosService } from '../../services/demos';
import { leadsAPI } from '../../services/api';
import { teachersService } from '../../services/teachers';
import TimeSelector from '../../components/TimeSelector';
import { useAuth } from '../../contexts/AuthContext';
import { filterLeadsEligibleForDemo } from '../../utils/leadDemoEligibility';
import DemoReadyStudentPicker from '../../components/Demos/DemoReadyStudentPicker';
import { formatDemoCountriesSchedulePreview } from '../../utils/dateTimeUtils';
import { hasMeetingUrl, normalizeMeetingUrlInput } from '../../utils/meetingUrl';
import {
  getTimezoneAbbreviation,
  getTimezoneOptions,
  normalizeTimezoneForFormSelect,
  normalizeTimezoneForStorage,
} from '../../utils/timezoneUtils';
import { getCountries } from '../../data/locationData';
import { formatDemoInstructorLabel } from '../../utils/demoInstructorLabel';

const CHANNEL_OPTIONS = [
  {
    value: 'crm_only',
    label: 'Only for our team',
    hint: 'Staff book children from the CRM. Not shown on the website.',
  },
  {
    value: 'landing_only',
    label: 'On the website',
    hint: 'Parents pick this slot on the landing page.',
  },
  {
    value: 'both',
    label: 'Team and website',
    hint: 'Shown in CRM and on the landing page.',
  },
];

function FormSection({ title, hint, children }) {
  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
        {title}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {hint}
        </Typography>
      ) : null}
      {children}
    </Grid>
  );
}

const DemoCreateForm = ({ open, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const browserTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  }, []);
  const userTimezone = user?.timezone || browserTimezone || 'UTC';
  const defaultTimezone = normalizeTimezoneForFormSelect(userTimezone);
  const [formData, setFormData] = useState({
    name: '',
    type: 'demo',
    lead_ids: [],
    student_ids: [],
    scheduled_date: null,
    scheduled_time: '',
    scheduled_timezone: defaultTimezone,
    duration_minutes: 30,
    teacher_id: '',
    notes: '',
    countries: [],
    channel: 'crm_only',
    capacity: 12,
    assigned_sales_user_id: '',
    meeting_url: '',
  });
  const [salespeople, setSalespeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [leads, setLeads] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [leadSearch, setLeadSearch] = useState('');

  const fetchTeachers = useCallback(async (sessionType) => {
    try {
      const res = await teachersService.getTeachersForSessionType(sessionType);
      const data = res?.data ?? res;
      setTeachers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading teachers:', err);
      setTeachers([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setFormData({
      name: '',
      type: 'demo',
      lead_ids: [],
      student_ids: [],
      scheduled_date: null,
      scheduled_time: '',
      scheduled_timezone: defaultTimezone,
      duration_minutes: 30,
      teacher_id: '',
      notes: '',
      countries: [],
      channel: 'crm_only',
      capacity: 12,
      assigned_sales_user_id: '',
      meeting_url: '',
    });
    setError('');
    setLeadSearch('');
    setLoading(true);

    const loadLeads = leadsAPI
      .getAll({ limit: 500 })
      .then((res) => {
        const list = res.data?.leads ?? res.data ?? [];
        return Array.isArray(list) ? filterLeadsEligibleForDemo(list) : [];
      })
      .then((leadList) => {
        setLeads(Array.isArray(leadList) ? leadList : []);
      })
      .catch((err) => {
        console.error('Error loading leads:', err);
        setLeads([]);
        setError((prev) => prev || 'Failed to load leads');
      });

    const loadTeachers = teachersService
      .getTeachersForSessionType('demo')
      .then((teacherRes) => {
        const teacherList = teacherRes?.data ?? teacherRes;
        setTeachers(Array.isArray(teacherList) ? teacherList : []);
      })
      .catch((err) => {
        console.error('Error loading teachers:', err);
        setTeachers([]);
        setError((prev) => prev || 'Failed to load teachers');
      });

    const loadSales = demosService
      .getSalespeople()
      .then((res) => {
        const list = res?.data ?? res ?? [];
        setSalespeople(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error('Error loading salespeople:', err);
        setSalespeople([]);
      });

    Promise.all([loadLeads, loadTeachers, loadSales]).finally(() => setLoading(false));
  }, [open, defaultTimezone]);

  const filteredLeads = useMemo(() => {
    if (!leadSearch?.trim()) return leads;
    const q = leadSearch.trim().toLowerCase();
    return leads.filter((l) => {
      const name = (l.name || '').toLowerCase();
      const fullName = [l.first_name, l.last_name].filter(Boolean).join(' ').toLowerCase();
      const phone = (l.phone || '').replace(/\D/g, '');
      const phoneQ = leadSearch.replace(/\D/g, '');
      const mother = (l.mother_name || '').toLowerCase();
      const father = (l.father_name || '').toLowerCase();
      const displayId = (l.display_id || '').toLowerCase();
      return (
        name.includes(q) ||
        fullName.includes(q) ||
        displayId.includes(q) ||
        (phoneQ && phone.includes(phoneQ)) ||
        mother.includes(q) ||
        father.includes(q)
      );
    });
  }, [leads, leadSearch]);

  const handleChange = (field, value) => {
    if ((field === 'lead_ids' || field === 'student_ids') && Array.isArray(value)) {
      value = value.map((id) => String(id));
    }
    if (field === 'type') {
      fetchTeachers(value);
      setFormData((prev) => ({ ...prev, type: value, teacher_id: '' }));
      setError('');
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.scheduled_date || !formData.scheduled_time) {
      setError('Pick a date and time');
      return;
    }
    const timeStr = formData.scheduled_time.includes(':') && formData.scheduled_time.length >= 8
      ? formData.scheduled_time
      : `${formData.scheduled_time}:00`;
    if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/.test(timeStr)) {
      setError('Invalid time format');
      return;
    }

    if (!formData.countries?.length) {
      setError('Select which countries this demo is for');
      return;
    }
    if (!hasMeetingUrl(formData.meeting_url)) {
      setError('Demo join link is required');
      return;
    }
    const landingChannel = formData.channel === 'landing_only' || formData.channel === 'both';
    if (landingChannel && !formData.assigned_sales_user_id) {
      setError('Pick a salesperson â€” they will own website bookings for this demo');
      return;
    }

    const dateLabel = dayjs(formData.scheduled_date).format('ddd D MMM');
    const autoName = `${dateLabel} Â· ${formData.countries.join(', ')}`;
    const demoName = formData.name.trim() || autoName;

    try {
      setSubmitting(true);
      setError('');
      await demosService.createDemo({
        name: demoName,
        type: formData.type,
        lead_ids: formData.lead_ids?.length ? formData.lead_ids : [],
        student_ids: formData.student_ids?.length ? formData.student_ids : [],
        scheduled_date: dayjs(formData.scheduled_date).format('YYYY-MM-DD'),
        scheduled_time: timeStr,
        scheduled_timezone: normalizeTimezoneForStorage(formData.scheduled_timezone || defaultTimezone) || 'UTC',
        duration_minutes: formData.duration_minutes,
        countries: formData.countries,
        channel: formData.channel,
        capacity: Number(formData.capacity) || 12,
        ...(formData.assigned_sales_user_id && { assigned_sales_user_id: formData.assigned_sales_user_id }),
        ...(formData.teacher_id && { teacher_id: formData.teacher_id }),
        ...(formData.notes?.trim() && { notes: formData.notes.trim() }),
        meeting_url: normalizeMeetingUrlInput(formData.meeting_url),
      });
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message
        || (Array.isArray(err.response?.data?.errors) ? err.response.data.errors.join(', ') : null)
        || err.response?.data?.error
        || 'Failed to create demo';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) onCancel();
  };

  const enteredTimezone = formData.scheduled_timezone || defaultTimezone;
  const enteredAbbr = getTimezoneAbbreviation(enteredTimezone);
  const countryPreview = useMemo(
    () => formatDemoCountriesSchedulePreview(
      formData.scheduled_date,
      formData.scheduled_time,
      enteredTimezone,
      formData.countries
    ),
    [formData.scheduled_date, formData.scheduled_time, enteredTimezone, formData.countries]
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create a demo</DialogTitle>
      <DialogContent sx={{ pt: 1.5, pb: 0 }} dividers>
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 3 }}>
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.25 }}>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setError('')}>
                  {error}
                </Alert>
              </Grid>
            )}

            <FormSection
              title="1. Who is this for?"
              hint="Pick the countries whose families can join. You can choose more than one."
            >
              <FormControl fullWidth size="small" required>
                <InputLabel id="demo-countries-label">Countries</InputLabel>
                <Select
                  labelId="demo-countries-label"
                  multiple
                  value={formData.countries}
                  onChange={(e) => handleChange('countries', e.target.value)}
                  input={<OutlinedInput label="Countries" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected || []).map((c) => (
                        <Chip key={c} label={c} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {getCountries().map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
                Where should families see this slot?
              </Typography>
              <RadioGroup
                value={formData.channel}
                onChange={(e) => handleChange('channel', e.target.value)}
              >
                {CHANNEL_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio size="small" />}
                    sx={{ alignItems: 'flex-start', ml: 0, mb: 0.5 }}
                    label={(
                      <Box sx={{ mt: 0.4 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{opt.hint}</Typography>
                      </Box>
                    )}
                  />
                ))}
              </RadioGroup>
            </FormSection>

            <Grid item xs={12}><Divider /></Grid>

            <FormSection
              title="2. When is it?"
              hint="Type the time on the clock parents use. If the demo is 4pm in New York, choose US Eastern and enter 4:00 â€” no need to convert to India time."
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Date"
                      value={formData.scheduled_date}
                      onChange={(date) => handleChange('scheduled_date', date)}
                      minDate={dayjs().subtract(10, 'day')}
                      slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="How long (minutes)"
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value, 10) || 30)}
                    inputProps={{ min: 15, max: 120, step: 15 }}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="demo-create-timezone-label">This time is on which clock?</InputLabel>
                    <Select
                      labelId="demo-create-timezone-label"
                      value={enteredTimezone}
                      label="This time is on which clock?"
                      onChange={(e) => handleChange('scheduled_timezone', e.target.value)}
                    >
                      {getTimezoneOptions().map((tz) => (
                        <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TimeSelector
                    label="Time"
                    value={formData.scheduled_time}
                    onChange={(value) => handleChange('scheduled_time', value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Demo join link"
                    placeholder="https://zoom.us/j/…"
                    value={formData.meeting_url}
                    onChange={(e) => handleChange('meeting_url', e.target.value)}
                    required
                    helperText="Required. Zoom, Meet, or other join URL. Sent to the landing page with this demo."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                    {!formData.scheduled_date || !formData.scheduled_time ? (
                      <Typography variant="body2">
                        Pick a date, clock, and time. We will show what families in each country will see.
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          You entered {countryPreview?.entered || 'â€”'} ({enteredAbbr}).
                        </Typography>
                        {formData.countries?.length ? (
                          (countryPreview?.countries || []).map((row) => (
                            <Typography key={row.country} variant="body2" sx={{ mt: 0.75 }}>
                              <strong>{row.country} families see:</strong> {row.line}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ mt: 0.75 }}>
                            Select countries in step 1 to preview their local times.
                          </Typography>
                        )}
                      </>
                    )}
                  </Alert>
                </Grid>
              </Grid>
            </FormSection>

            <Grid item xs={12}><Divider /></Grid>

            <FormSection
              title="3. Who runs it?"
              hint={formData.channel === 'crm_only'
                ? 'Teacher is optional. Salesperson is optional for team-only demos.'
                : 'Website bookings are always assigned to the salesperson you pick here.'}
            >
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" required={formData.channel !== 'crm_only'}>
                    <InputLabel>Salesperson</InputLabel>
                    <Select
                      value={formData.assigned_sales_user_id}
                      label="Salesperson"
                      onChange={(e) => handleChange('assigned_sales_user_id', e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {salespeople.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.first_name} {s.last_name}
                          {s.role && s.role !== 'sales' ? ` (${String(s.role).replace(/_/g, ' ')})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {formData.channel === 'crm_only'
                        ? 'Optional'
                        : 'Required â€” new website registrations go to this person'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Teacher (optional)</InputLabel>
                    <Select
                      size="small"
                      value={formData.teacher_id}
                      onChange={(e) => handleChange('teacher_id', e.target.value)}
                      label="Teacher (optional)"
                    >
                      <MenuItem value="">None yet</MenuItem>
                      {teachers.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {formatDemoInstructorLabel(t)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="How many children?"
                    type="number"
                    inputProps={{ min: 1, max: 50 }}
                    value={formData.capacity}
                    onChange={(e) => handleChange('capacity', parseInt(e.target.value, 10) || 12)}
                    helperText="Seat limit. Default is 12."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Demo or trial</InputLabel>
                    <Select
                      size="small"
                      value={formData.type}
                      onChange={(e) => handleChange('type', e.target.value)}
                      label="Demo or trial"
                    >
                      <MenuItem value="demo">Demo</MenuItem>
                      <MenuItem value="trial">Trial</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </FormSection>

            <Grid item xs={12}><Divider /></Grid>

            <FormSection
              title="4. Add people now? (optional)"
              hint="You can skip this and add children later from the demo page."
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search by child, phone, or parent..."
                value={leadSearch}
                onChange={(e) => setLeadSearch(e.target.value)}
                sx={{ mb: 1 }}
              />
              <FormControl fullWidth size="small">
                <InputLabel id="demo-create-leads-label">Leads to add</InputLabel>
                <Select
                  labelId="demo-create-leads-label"
                  id="demo-create-leads-select"
                  multiple
                  size="small"
                  value={formData.lead_ids || []}
                  onChange={(e) => handleChange('lead_ids', e.target.value)}
                  label="Leads to add"
                  inputProps={{ 'aria-label': 'Select lead(s)' }}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 300 } },
                    disableScrollLock: true
                  }}
                  renderValue={(selected) =>
                    (selected || [])
                      .map((id) => {
                        const lead = leads.find((l) => String(l.id) === String(id));
                        if (!lead) return String(id);
                        const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.name || '';
                        const parent = lead.mother_name || lead.father_name || '';
                        const parts = [name, lead.phone, parent].filter(Boolean);
                        return parts.join(' | ');
                      })
                      .join(', ')
                  }
                >
                  {filteredLeads.map((lead) => {
                    const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.name || '';
                    const parent = lead.mother_name || lead.father_name || '';
                    const label = [name, lead.phone, parent ? `Parent: ${parent}` : ''].filter(Boolean).join(' | ');
                    return (
                      <MenuItem key={lead.id} value={String(lead.id)}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Box sx={{ mt: 1.5 }}>
                <DemoReadyStudentPicker
                  selectedIds={formData.student_ids || []}
                  onChange={(ids) => handleChange('student_ids', ids)}
                  disabled={submitting}
                />
              </Box>
            </FormSection>

            <Grid item xs={12}><Divider /></Grid>

            <FormSection
              title="5. Name (optional)"
              hint="Leave blank and we will name it from the date and countries."
            >
              <TextField
                fullWidth
                size="small"
                label="Demo name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder={
                  formData.scheduled_date && formData.countries?.length
                    ? `${dayjs(formData.scheduled_date).format('ddd D MMM')} Â· ${formData.countries.join(', ')}`
                    : 'e.g. Saturday USA discovery'
                }
              />
              <TextField
                fullWidth
                size="small"
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                sx={{ mt: 1.5 }}
              />
            </FormSection>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || submitting || !formData.scheduled_date || !formData.scheduled_time || !formData.countries?.length || !hasMeetingUrl(formData.meeting_url)}
        >
          {submitting ? 'Creating...' : 'Create demo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemoCreateForm;
